import EnvService from "../../../env";
import {matchPath} from "react-router-dom";
import type {Method as AxiosApiMethods} from "axios";
import {ApiMethods} from "../../../../../@bizkeytech/base/core";


/**
 * This interface houses all the available endpoints of this application specific for the 'biz-print' server.
 */
class ApiEndpoints {

    /**
     * The base url of the api endpoints of this application.
     * @private
     */
    private static baseUrl: string = EnvService.BizProductionApiUrl.concat('/api');

    /**
     * The base url of the component state api endpoints of this application.
     * @private
     */
    private static componentStateBaseUrl: string = EnvService.componentStateApiUrl.concat('/api');

    /**
     * The base url of the api endpoints of this application for version 1.
     * @private
     */
    private static v1BaseUrl = this.baseUrl.concat("");

    /**
     * The controllers of the api endpoints for version 1.
     * @private
     */
    private static v1 = {
        bizFile: this.v1BaseUrl.concat("/BizFile"),
        setting: this.v1BaseUrl.concat("/Setting"),
        reportingTools: this.v1BaseUrl.concat("/ReportingTools"),
        uiComponent: this.componentStateBaseUrl.concat("/UIComponent"),
        employeeProfiles: this.v1BaseUrl.concat("/EmployeeProfiles"),
        operator: this.v1BaseUrl.concat("/Operator"),
        planning: this.v1BaseUrl.concat("/Planning"),
        purchasePlanning: this.v1BaseUrl.concat("/Purchasing"),
        inventory: this.v1BaseUrl.concat("/Inventory"),
    }

    // ********************************************** V1 Endpoints **********************************************

    /**
     * The endpoints specific for the biz file controller of the server api.
     */
    public static bizFile = {
        baseUrl: this.v1.bizFile,
        uploadRemainingChunk: this.v1.bizFile.concat("/UploadRemainingChunk"),
        createAccessToken: this.v1.bizFile.concat("/CreateAccessToken"),
        uploadFile: this.v1.bizFile.concat("/SmallFileOrInitiateUpload"),
        createStorageAccount: this.v1.bizFile.concat("/CreateStorageAccount"),
        getStorageAccount: this.v1.bizFile.concat("/GetStorageAccount"),
    }

    /**
     * The endpoints specific for the config controller of the server api.
     */
    public static setting = {
        baseUrl: this.v1.setting,
        getPartnerSetting: this.v1.setting.concat('/GetPartnerSettings'),
    }

    /**
     * The endpoints specific for the reporting tools controller of the server api.
     */
    public static reportingTools = {
        baseUrl: this.v1.reportingTools,
        getColumns: this.v1.reportingTools.concat(`/GetColumns`),
    }

    /**
     * The endpoints specific for the data-grid controller of the server api.
     */
    public static uiComponent = {
        baseUrl: this.v1.uiComponent,
        getData: this.v1.uiComponent.concat('/GetApplicationComponents'),
        saveData: this.v1.uiComponent.concat('/Save'),
    }

    /**
     * The endpoints specific for the employee profiles controller of the server api.
     */
    public static operator = {
        baseUrl: this.v1.operator,
        getCurrentProfile: `${this.v1.operator}/GetOperator`,
    }

    /**
     * The endpoints specific for the planning controller of the server api.
     */
    public static planning = {
        baseUrl: this.v1.planning,
        buildPlan: this.v1.planning.concat("/BuildPlan"),
        list: this.v1.planning.concat("/List"),
        getInformation: this.v1.planning,
        delete: this.v1.planning,
        updatePlanningProperties: this.v1.planning.concat("/UpdatePlanningProperties"),
    }

    /**
     * The endpoints specific for the purchase planning controller of the server api.
     */
    public static purchasePlanning = {
        baseUrl: this.v1.purchasePlanning,
        buildPlan: this.v1.purchasePlanning.concat("/CreatePlan"),
        getInformation: this.v1.purchasePlanning.concat('/GetPlan'),
        getPartsByPrefix: this.v1.purchasePlanning.concat('/GetPartsByPrefix'),
        updatePlanDetails: this.v1.purchasePlanning.concat('/UpdatePlanDetails'),
        getSalesReport: this.v1.purchasePlanning.concat('/GetSalesReport'),
        getWarrantyReturnDetails: this.v1.purchasePlanning.concat('/GetWarrantyReturnDetails'),
    }


    /**
     * The endpoints specific for the inventory controller of the server api.
     */
    public static inventory = {
        baseUrl: this.v1.inventory,
        autoComplete: this.v1.inventory.concat('/AutoComplete'),
        validateParts: this.v1.inventory.concat('/ValidateParts'),
        getPartsUsingRawMaterial: this.v1.inventory.concat('/GetPartsUsingRawMaterial'),
    }

    // ********************************************** Intents **********************************************


    /**
     * The intent descriptions for each API endpoint.
     * @private
     */
    private static apiIntents: { [key: string]: string | { [method: string]: string } } = {
        [this.bizFile.uploadRemainingChunk]: "Upload a remaining chunk of a file.",
        [this.bizFile.createAccessToken]: "Create an access token for a file.",
        [this.bizFile.createStorageAccount]: "Create a storage account for the partner with default capacity value.",
        [this.bizFile.getStorageAccount]: "Get the information of the current storage account.",
        [this.setting.getPartnerSetting]: "Fetch system settings.",
        [this.reportingTools.getColumns]: "Retrieve reporting tools data including columns.",
        [this.uiComponent.getData]: "Retrieve application components data.",
        [this.uiComponent.saveData]: "Save application components data.",
        [this.operator.getCurrentProfile]: "Get current employee profile.",
        [this.planning.getInformation]: {
            [ApiMethods.get]: "Fetch the planning session information.",
            [ApiMethods.delete]: "Delete a planning session.",
        },
        [this.planning.list]: "Search for planning sessions.",
        [this.planning.buildPlan]: "Calculate a plan.",
        [this.planning.updatePlanningProperties]: "Update planning session.",
        [this.purchasePlanning.getInformation]: {
            [ApiMethods.get]: "Fetch the purchase planning session information.",
        },
        [this.purchasePlanning.getPartsByPrefix]: "Get parts by prefix.",
        [this.purchasePlanning.buildPlan]: "Calculate a purchase plan.",
        [this.purchasePlanning.updatePlanDetails]: "Update one purchase-plan row's quantity and note.",
        [this.inventory.autoComplete]: "Autocomplete parts.",
        [this.inventory.validateParts]: "Validate parts.",
        [this.inventory.getPartsUsingRawMaterial]: "Get parts using raw material.",
    };

    /**
     * Get the intent description for a given API endpoint and method.
     *
     * @param endpoint      the API endpoint.
     * @param method        the HTTP method.
     * @returns             the intent description.
     */
    public static getIntent(endpoint: string, method?: AxiosApiMethods): string | undefined {
        const normalize = (str: string) => str
            .replace(this.baseUrl, '')
            .replace(/\?.*$/, '')
        const normalizedEndpoint = normalize(endpoint);
        const endpoints = Object.keys(this.apiIntents);
        const matchedEndpoints = endpoints
            .map(path => matchPath(normalizedEndpoint, {path: normalize(path), exact: false, sensitive: false}))
            .map((e, index) => {
                if (!e) return e;
                return endpoints[index];
            })
            .filter(match => !!match)
        if (matchedEndpoints.length <= 0) {
            return undefined;
        }

        let matchedEndpoint;
        if (matchedEndpoints.length > 1) {
            matchedEndpoint = matchedEndpoints.find(e => e === endpoint) ?? matchedEndpoints[0];
        } else {
            matchedEndpoint = matchedEndpoints[0]
        }

        const intent = this.apiIntents[matchedEndpoint!];
        if (typeof intent === 'string') {
            return intent;
        }
        return intent?.[method ?? ""];
    }
}

export default ApiEndpoints;
